import type { RealMachinesDatabase } from '@/db/database'
import { DatabaseOperationError } from '@/db/errors'
import { auditLogSchema, counterSchema } from '@/db/schemas'
import { createUuid } from '@/db/uuid'
import type { AuditAction, AuditableEntity, CounterKey } from '@/db/types'

const prefixes: Record<CounterKey, string> = {
  clientCode: 'CLI',
  machineCode: 'MAQ',
  reportNumber: 'RM',
}

export async function nextCode(
  database: RealMachinesDatabase,
  key: CounterKey,
  now: string,
): Promise<string> {
  const current = await database.counters.get(key)
  const nextValue = (current?.value ?? 0) + 1
  if (nextValue > 999_999) {
    throw new DatabaseOperationError(
      'conflict',
      `O contador ${key} atingiu o limite de seis dígitos.`,
      'A numeração atingiu o limite permitido. É necessária uma atualização do formato.',
    )
  }
  await database.counters.put(counterSchema.parse({ key, value: nextValue, updatedAt: now }))
  return `${prefixes[key]}-${String(nextValue).padStart(6, '0')}`
}

export async function writeAuditLog(
  database: RealMachinesDatabase,
  entityType: AuditableEntity,
  entityId: string,
  action: AuditAction,
  changes: Record<string, unknown>,
  occurredAt: string,
): Promise<void> {
  await database.auditLogs.add(
    auditLogSchema.parse({
      id: createUuid(),
      entityType,
      entityId,
      action,
      occurredAt,
      changes,
    }),
  )
}

export function changedFields(input: object): Record<string, unknown> {
  return { fields: Object.keys(input).sort() }
}

export function requireRecord<T>(record: T | undefined, entityName: string): T {
  if (!record) {
    throw new DatabaseOperationError(
      'not_found',
      `${entityName} não encontrado.`,
      `${entityName} não encontrado. Atualize a lista e tente novamente.`,
    )
  }
  return record
}
